/**
 * How many lights a material may bind on THIS machine.
 *
 * Field failure (2026-07-30, launch day, player console): "VERTEX shader uniform block
 * count exceeds GL_MAX_VERTEX_UNIFORM_BUFFERS (12)". Babylon gives every bound light its
 * own uniform buffer, on top of three fixed ones (Scene, Material, Mesh). Our materials
 * asked for twelve lights — fifteen blocks — while the WebGL2 spec only guarantees
 * twelve, and plenty of Windows drivers report exactly the guarantee. On those machines
 * the shader can never link, at any quality tier, Babylon's fallback ladder included:
 * the boot hung at "almost ready" until the stall watchdog gave up. It never reproduced
 * on Apple GPUs, which report sixteen.
 *
 * So the twelve is now a ceiling, not a constant: the budget is measured from the
 * context's real limit. Capable GPUs keep the full twelve; a minimum-spec context gets
 * nine and BOOTS — the last three flood lamps go inert there, which is the correct
 * trade. Anyone adding another uniform BUFFER to these materials (not a plain uniform —
 * the mist plugin's floats don't count) must raise RESERVED_UNIFORM_BLOCKS with it.
 */

import type { Scene } from '@babylonjs/core/scene';

/** The most lights any material asks for; see rigMaterials for why not more. */
export const LIGHT_CEILING = 12;

/** Babylon's Scene + Material + Mesh uniform buffers, bound before any light's. */
export const RESERVED_UNIFORM_BLOCKS = 3;

/** WebGL2's guaranteed minimum for MAX_VERTEX/FRAGMENT_UNIFORM_BLOCKS. */
export const MIN_SPEC_BLOCKS = 12;

/**
 * Pure budget rule, unit-tested: unknown limits are treated as minimum-spec, because a
 * capable GPU shorted three lights is a dimmer corner of the rig while a minimum-spec
 * GPU handed twelve is a game that never boots.
 */
export function lightBudget(uniformBlockLimit: number | null): number {
  const limit =
    typeof uniformBlockLimit === 'number' && Number.isFinite(uniformBlockLimit) && uniformBlockLimit > 0
      ? uniformBlockLimit
      : MIN_SPEC_BLOCKS;
  return Math.max(1, Math.min(LIGHT_CEILING, Math.floor(limit) - RESERVED_UNIFORM_BLOCKS));
}

/**
 * The context's own limit — vertex and fragment stages each have one; the binding
 * counts against both, so the budget takes the tighter.
 *
 * Read through the public canvas rather than Babylon internals: asking a canvas for a
 * context type it already holds returns the live context, never a new one. A headless
 * or WebGL1 engine answers null and falls back to the minimum-spec budget.
 */
export function uniformBlockLimit(scene: Scene): number | null {
  const canvas = scene.getEngine().getRenderingCanvas();
  const gl = canvas?.getContext?.('webgl2');
  if (!gl) return null;
  const vertex = Number(gl.getParameter(gl.MAX_VERTEX_UNIFORM_BLOCKS));
  const fragment = Number(gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_BLOCKS));
  if (!Number.isFinite(vertex) || !Number.isFinite(fragment)) return null;
  return Math.min(vertex, fragment);
}

export function maxSimultaneousLightsFor(scene: Scene): number {
  return lightBudget(uniformBlockLimit(scene));
}
